import { Button, FormControl, LinearProgress, Radio, Chip } from "@mui/material";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/system";
import * as React from "react";
import TextInput from "../TextInput/TextInput";
import classes from './MUIMultiSelect.module.css';
import Alert from "../Alert/Alert";
import Modal from "../../Modal/Modal";
import AddItemModal from "../MUIMultiItemBox/AddItemModal"; 

const CustomFormControl = styled(FormControl)({
  "& .MuiNativeSelect-select": {
    borderRadius: 4,
    padding: 9,
    fontSize: 14,
  },
});

const MUIMultiSelect = React.forwardRef(function MUIMultiSelect(
  {
    label,
    options,
    source,
    placeholder = "Select",
    value = [],
    onChange = () => { },
    onRadioChange = () => { }, // Callback for radio button changes
    loading,
    disabled,
    selectedPrimary = null,
    allowCustomOption = false,
    allowSettingPrimary = false,
    shouldShowValue = true,
    customWidth = null,
    onCustomConfirm=()=>{},
    modalValidation,
    onChipClick = null,       
    modalFields = null,      
    onModalSave = null,   
    ...props
  },
  ref
) {
  //const [value, setValue] = React.useState(
  //  defaultValue.length > 0 ? defaultValue : [] // Default to the first menu item
  //);
  const [customOptions, setCustomOptions] = React.useState([]);
  const [newCustomOption, setNewCustomOption] = React.useState("");
  const [selectedRadio, setSelectedRadio] = React.useState(selectedPrimary);
  const [selectedValues, setSelectedValues] = React.useState(value || []);
  const [modalOpen, setModalOpen] = React.useState(false);

  const {
    modalLoading = false,
    errorMessage = null,
    errorTitle = null,
    onCustomInputChange = () => { },
    setErrorMessage = () => { }
  } = modalValidation || {};

  React.useImperativeHandle(ref, () => ({
    getValue: () => value,
  }));

  React.useEffect(() => {
    if (value && value.length > 0) {
      setSelectedValues(value);
      setSelectedRadio(selectedPrimary); // Set the first value as the selected radio button   
    } else {
      setSelectedValues([]);
      setSelectedRadio(null); // Reset radio button if no value is selected
    }
  }, [value, selectedPrimary]);

  const handleChange = (event) => {
    const selectedValues = event.target.value;
    console.log("Selected values:", selectedValues);
    if (selectedValues.includes("addManually")) {
      onChange(selectedValues, selectedRadio || selectedValues[0]);
      return;
    }

    // If the deselected item was the one with the active radio button, reset the radio button
    if (!selectedValues.includes(selectedRadio)) {
      setSelectedRadio(null);
      onRadioChange(null);
    }
    if (selectedValues && selectedValues.length > 0 && !selectedValues.includes(selectedRadio)) {
      setSelectedRadio(selectedValues[0]);
    }
    setSelectedValues(selectedValues);
    onChange(selectedValues, selectedRadio || selectedValues?.[0]);
  };

  // ─── Existing single-input add (used when modalFields is NOT provided) ────
  const handleAddCustomOption = () => {
    const trimmedOption = newCustomOption.trim();
    if (
      trimmedOption &&
      !errorTitle &&
      !customOptions.includes(trimmedOption)
    ) {
      setCustomOptions((prev) => [...prev, trimmedOption]);
      setNewCustomOption("");
      onCustomConfirm(trimmedOption);
    }
    onChange([...value, trimmedOption]);
  };

  // ───  multi-field AddItemModal save handler ───────────────────────────
  const handleModalFieldsSave = (formData) => {
    setModalOpen(false);
    if (onModalSave) {
      onModalSave(formData); 
    }
  };

  const handleRadioChange = (optionValue) => {
    setSelectedRadio(optionValue);
    onRadioChange(optionValue);

    // Ensure the corresponding menu item remains selected
    if (!value.includes(optionValue)) {
      onChange([...value, optionValue], optionValue);
    } else {
      onChange(value, optionValue);
    }
  };

  const handleChipDelete = (valueToRemove) => (event) => {
    event.stopPropagation();
    event.preventDefault();
    
    const newSelectedValues = selectedValues.filter(val => val !== valueToRemove);
    
    // If the deleted item was the one with the active radio button, reset the radio button
    if (valueToRemove === selectedRadio) {
      setSelectedRadio(null);
      onRadioChange(null);
    }
    
    setSelectedValues(newSelectedValues);
    onChange(newSelectedValues, selectedRadio === valueToRemove ? null : selectedRadio);
  };

  const renderPrimarySelection = (option) => {
    if (allowSettingPrimary) {
      return (
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", width: "100%" }}>
          <Radio
            size="small"
            checked={selectedRadio === option.value}
            onChange={() => handleRadioChange(option.value)}
            onClick={(e) => {
              e.stopPropagation();
            }}
          />
          {selectedRadio === option.value && (
            <Typography variant="caption" color="primary" style={{ marginLeft: 4 }}>
              (primary)
            </Typography>
          )}
        </div>
      );
    }
  };

  const renderReadOnlyValue = () => {
    if (!value || value.length === 0) {
      return (
        <Box sx={{ minHeight: '28px', display: 'flex', alignItems: 'center' }}>
          <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>{placeholder}</span>
        </Box>
      );
    }

  // Determine display names based on whether renderSelectedValues is provided
    let displayItems = [];
  
    if (props.renderSelectedValues) {
      const renderedText = props.renderSelectedValues(value);
      displayItems = renderedText.split(', ');
    } else {
      displayItems = value.map((selectedValue) => {
        const option = options.find(opt => opt.value === selectedValue);
        const customOption = customOptions.find(opt => opt === selectedValue);
        return option ? option.name : (customOption || selectedValue);
      });
    }

    return (
      <Box display="flex" flexWrap="wrap" gap={0.75} alignItems="center">
        {displayItems.map((displayName, index) => (
          <Chip
            sx={{
              mt: 0.5,
              height: "auto",
              maxWidth: "100%",
              "& .MuiChip-label": {
                whiteSpace: "normal",
                wordBreak: "break-word",
                lineHeight: 1.3,
                paddingTop: "4px",
                paddingBottom: "4px"
              }
            }}
            key={index}
            label={displayName}
          />
        ))}
      </Box>
    );
  };

  // Render selected values as chips inside the input
  const renderSelectedChips = (selected) => {
    if (!selected || selected.length === 0) {
      return <span style={{ color: '#9ca3af' }}>{placeholder}</span>;
    }
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', minHeight: '24px' }}>
        {selected.map((selectedValue) => {
          const option = options.find(opt => opt.value === selectedValue);
          const customOption = customOptions.find(opt => opt === selectedValue);
          const displayName = option ? option.name : (customOption || selectedValue);

          return (
            <Chip
              key={selectedValue}
              label={displayName}
              size="small"
              onClick={onChipClick ? (e) => {
                e.stopPropagation();
                onChipClick(selectedValue);
              } : undefined}
              onDelete={handleChipDelete(selectedValue)}
              onMouseDown={(e) => e.stopPropagation()}
              sx={{
                height: "auto",
                maxWidth: "100%",
                margin: "2px 0",
                cursor: onChipClick ? "pointer" : "default", 
                "& .MuiChip-label": {
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  lineHeight: 1.3,
                  paddingTop: "4px",
                  paddingBottom: "4px",
                  paddingRight: "4px",
                },
                "& .MuiChip-deleteIcon": {
                  fontSize: "18px",
                  margin: "0 4px 0 -2px",
                }
              }}
            />
          );
        })}
      </Box>
    );
  };

  // Read-only mode
  const isReadOnly = disabled;

  if (isReadOnly) {
    return (
      <Box className={classes.box}>
        <Box className={customWidth ? customWidth : classes.boxSection}>
          <Typography className={classes.label}>{label}</Typography>
          <CustomFormControl fullWidth variant="outlined" size="small">
            <Box
              sx={{
                minHeight: '40px',
                display: 'flex',
                alignItems: 'center',
                padding: '8px 14px',
                border: '1px solid #e5e7eb',
                borderRadius: '4px',
                backgroundColor: '#f8fafc',
                pointerEvents: 'none',
              }}
            >
              {renderReadOnlyValue()}
            </Box>
          </CustomFormControl>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.box}>
      <Box className={customWidth ? customWidth : classes.boxSection}>
        <Typography className={classes.label}>{label}</Typography>
        <CustomFormControl fullWidth variant="outlined" size="small">
          {loading && <LinearProgress className={classes.progress} />}
          <Select
            multiple={true}
            value={selectedValues}
            onChange={handleChange}
            size="small"
            sx={{
              minHeight: "40px",
              "& .MuiSelect-select": {
                paddingTop: "6px",
                paddingBottom: "6px",
                display: "flex",
                alignItems: "center",
                minHeight: "28px",
              }
            }}
            input={<OutlinedInput />}
            renderValue={(selected) => {
              if (shouldShowValue) {
                return renderSelectedChips(selected);
              }
              return placeholder;
            }}
            {...props}
            filterOptions={(x) => x}
            MenuProps={{
              disableAutoFocus: true,
              autoFocus: false,
              disableAutoFocusItem: true,
            }}
          >
            {options.length > 0 &&
              options.map((option) => (
              <MenuItem dense={true} key={option.value} value={option.value}>
                  {/* MUI Checkbox */}
                {option.value === "addManually" ? (
                  <span>{option.name}</span>
                ) : (
                  <>
                    <input
                      type="checkbox"
                      checked={selectedValues.indexOf(option.value) > -1}
                      style={{ marginRight: 8 }}
                      readOnly
                    />
                    {option.name}
                    {renderPrimarySelection(option)}
                  </>
                )}
              </MenuItem>
            ))}
            {customOptions.map((customOption) => (
              <MenuItem dense={true} key={customOption} value={customOption}>
                <input
                  type="checkbox"
                  checked={selectedValues.indexOf(customOption) > -1}
                  readOnly
                  style={{ marginRight: 8 }}
                />
                {customOption}
              </MenuItem>
            ))}
          </Select>
        </CustomFormControl>
      </Box>

      {allowCustomOption && (
        <Box>
          <Button
            onClick={() => {
              setModalOpen(true);
              setNewCustomOption("");
            }}
            variant="contained"
            size="small"
            className={classes.addButton}
            disabled={options?.length === 0}
          >
            +
          </Button>
        </Box>
      )}


      {allowCustomOption && (
        modalFields ? (
          // ───  multi-field modal via AddItemModal ──────────────────
          <AddItemModal
            open={modalOpen}
            onClose={() => {
              setModalOpen(false);
              onCustomConfirm("");
            }}
            onSave={handleModalFieldsSave}
            title={`Add ${label}`}
            fields={modalFields}
            useObjectFormat={true}
          />
        ) : (
          // ─── single text input modal — completely unchanged ─
          <Modal
            title={`Add New ${label}`}
            open={modalOpen}
            showActions
            handleClose={() => {
              setModalOpen(false);
              setErrorMessage("");
              onCustomConfirm("");
            }}
            handleConfirm={() => {
              handleAddCustomOption();
              setModalOpen(false);
            }}
            confirmBtnDisabled={!newCustomOption.trim() || modalLoading}
          >
            <Box className={classes.inputWrapper}>
              <TextInput
                autoFocus
                fullWidth
                value={newCustomOption}
                label={label}
                onChange={(e) => {
                  const val = e.target.value;
                  setNewCustomOption(val);
                  onCustomInputChange(val);
                }}
              />
            {modalLoading && (
              <LinearProgress className={classes.progress}/>
            )}
            </Box>

            {errorMessage && (
              <Box className={classes.alertWrapper}>
                <Alert title={errorTitle} message={errorMessage} />
              </Box>
            )}
          </Modal>
        )
      )}
    </Box>
  );
});

export default MUIMultiSelect;
